import moment from 'moment';
import { ProducedDocumentsWritingOffHeader } from 'store/declarations/common/produced-document-writing-off-header';
import { DeclarationName } from 'store/declarations/enums/common/declaration-name';
import { h1PathBox44, h7PathBox44 } from './mappers';
import { Box44FieldSettings, Box44FormValues, FormikControls } from './types';
import { Box44Structure } from 'views/declarations/ireland/import/utils';

export const getDateFormatForBox44 = (value: moment.Moment | null) =>
    value ? moment(value).format('YYYYMMDDHHmm') : null;

export const updateBox44 = (
    item: Required<Omit<ProducedDocumentsWritingOffHeader, 'id'>>,
    box44Items: Record<string, any>
) => {
    const box44 = { ...box44Items };

    if (item.documentType) {
        box44[item.documentType] = {
            documentType: item.documentType,
            documentIdentifier: item.documentIdentifier,
        };
    } else {
        delete box44[item.documentType];
    }

    return box44;
};

export const getBox44Path = (declarationType: DeclarationName) => {
    return declarationType === DeclarationName.H7 ? h7PathBox44 : h1PathBox44;
};

export const getBox44FieldPath = (box44: Box44FieldSettings) => {
    return `${box44.path}.${box44.type}.${box44.keyNames.documentIdentifier}`;
};

export const handleBox44Field = (
    value: string | number | null,
    controls: FormikControls,
    box44: Box44FieldSettings,
    template: {
        template?: boolean | undefined;
        form?: string | undefined;
    }
) => {
    const pathToBox44 = template?.template ? `${template.form}.defaults.${box44.path}` : box44.path;
    const box44Value = (controls.getFieldProps(pathToBox44).value ?? {}) as Record<string, any>;
    const existingField = box44Value[box44.type];
    // A complete entry must include documentType. Partial writes like
    // { documentIdentifier } (e.g. from DeclarationInput fieldHelpers.setValue on the nested path)
    // must be treated as missing so we recreate the full object.
    const hasCompleteField =
        existingField?.[box44.keyNames.documentType] && existingField?.[box44.keyNames.documentIdentifier];

    const createBox44Field = () => {
        controls.getFieldHelpers(`${pathToBox44}.${box44.type}`).setValue({
            ...(typeof existingField === 'object' && existingField ? existingField : {}),
            [box44.keyNames.documentType]: box44.type,
            [box44.keyNames.documentIdentifier]: value,
        });
    };
    const updateBox44Field = () => {
        controls.getFieldHelpers(`${pathToBox44}.${box44.type}.${box44.keyNames.documentIdentifier}`).setValue(value);
    };
    const deleteBox44Field = () => {
        const box44ValueCopy = { ...box44Value };
        delete box44ValueCopy[box44.type];
        controls.getFieldHelpers(pathToBox44).setValue(box44ValueCopy);
    };

    if (hasCompleteField && existingField[box44.keyNames.documentIdentifier] === value) {
        return { status: 'unchanged' };
    }

    if (value) {
        if (hasCompleteField) {
            updateBox44Field();
            return { status: 'updated' };
        } else {
            createBox44Field();
            return { status: 'created' };
        }
    } else if (existingField) {
        deleteBox44Field();
        return { status: 'deleted' };
    }

    return { status: 'unchanged' };
};

export const createInitialValues = (fields: Box44Structure): Box44FormValues => {
    const values: Box44FormValues = {
        authorityNameSubmitter: undefined,
        date: undefined,
        quantity: undefined,
        amount: undefined,
    };

    if (fields.documentType) {
        values[fields.documentType] = undefined;
    }

    if (fields.documentIdentifier) {
        values[fields.documentIdentifier] = undefined;
    }

    if (fields.measurement) {
        values[fields.measurement] = undefined;
    }

    if (fields.monetaryAmount) {
        values[fields.monetaryAmount] = undefined;
    }

    return values;
};

export const getBox44StorageKey = (entry: Record<string, any>, structure: Box44Structure): string | undefined => {
    if (structure.documentType && hasValue(entry[structure.documentType])) {
        return String(entry[structure.documentType]);
    }

    if (structure.measurement && hasValue(entry[structure.measurement]) && hasValue(entry.quantity)) {
        return String(entry[structure.measurement]);
    }

    if (structure.monetaryAmount && hasValue(entry[structure.monetaryAmount]) && hasValue(entry.amount)) {
        return String(entry[structure.monetaryAmount]);
    }

    return undefined;
};

const hasValue = (value: unknown) => value !== undefined && value !== null && value !== '';

export const createBox44FieldMap = (
    documentTypeName?: string,
    documentIdentifierName?: string,
    measurementName?: string,
    monetaryAmountName?: string
): Box44Structure => ({
    documentType: documentTypeName,
    documentIdentifier: documentIdentifierName,
    measurement: measurementName,
    monetaryAmount: monetaryAmountName,
});
